import { useProductContext } from "../../../context/ProductContext";
import { addHeapGroupChanged } from "../../../services/elevar/events";
import { getOptionImage } from "../../../util/getOptionImage";
import type { ProductOptionPickerProps } from "..";
import {
  getActiveGroup,
  getGroupIdentifier,
  makeGroupChangeHandler,
} from "../util/groups";

import ClassicGroupPickerV2 from "./ClassicGroupPickerV2";
import OptionModal from "./OptionModal";
import OptionPicker from "./OptionPicker";
import RichGroupV2 from "./RichGroupV2";

export interface RichGroupPickerProps
  extends Omit<ProductOptionPickerProps, "values" | "type"> {
  groups: NonNullable<ProductOptionPickerProps["groups"]>;
}

export default function RichGroupPickerV2({
  groups,
  name,
  onChange,
  selectedValue,
  modal,
}: RichGroupPickerProps) {
  const {
    product: { optionImages },
    selectedOptions,
  } = useProductContext();
  const formattedGroups = groups.map((group) => {
    const identifier = getGroupIdentifier(selectedValue)(group);
    const combinedOptions = {
      ...selectedOptions,
      [name]: identifier,
    };

    const matchingImage = getOptionImage(optionImages, combinedOptions);

    return {
      label: group.label || group.name,
      value: group.name,
      image: matchingImage?.image,
      identifier,
    };
  });

  const activeGroup = getActiveGroup(selectedValue, groups);
  if (!activeGroup) return null;

  const handleChangeWithTracking = makeGroupChangeHandler(
    name,
    groups,
    onChange,
    (value) => addHeapGroupChanged(name, value)
  );
  const handleChange = makeGroupChangeHandler(name, groups, onChange);

  return (
    <div className="flex flex-col gap-y-6">
      <div className="flex flex-col gap-y-3">
        <OptionPicker
          type="graphic-square"
          name={name}
          values={formattedGroups}
          onChange={handleChangeWithTracking}
          gridColumns={4}
          selectedValue={activeGroup.name}
          mobileOrder={1}
          fullWidth
        />

        {modal && (
          <OptionModal
            {...modal}
            richGroupPicker={RichGroupV2}
            groupPicker={ClassicGroupPickerV2}
            optionPicker={OptionPicker}
          />
        )}
      </div>

      {activeGroup.groups && (
        <ClassicGroupPickerV2
          type={activeGroup.type}
          name={activeGroup.name}
          groups={activeGroup.groups}
          gridColumns={activeGroup.gridColumns ?? 3}
          label={activeGroup.title}
          mobileOrder={1}
          selectedValue={selectedValue}
          onChange={handleChange}
          showNameAboveOptions
          isNested
          fullWidth
          modal={activeGroup.modal ?? null}
        />
      )}
    </div>
  );
}
